import { cloneDeep } from 'lodash-es';
import type { RouteParams } from 'vue-router';
import { isHttpUrl, treeHelper } from '@mts-fe/utils';
import type { AppRouteModule, AppRouteRecordRaw, Menu } from '@/router/types';

export function getAllParentPath<T = Recordable>(treeData: T[], path: string) {
  const menuList = treeHelper.findPath(treeData, (n) => n.path === path) as Menu[];
  return (menuList || []).map((item) => item.path);
}

// 路径处理
export function joinParentPath(menus: Menu[], parentPath = '') {
  for (let index = 0; index < menus.length; index++) {
    const menu = menus[index];
    // https://next.router.vuejs.org/guide/essentials/nested-routes.html
    // Note that nested paths that start with / will be treated as a root path.
    // 请注意，以 / 开头的嵌套路径将被视为根路径。
    // This allows you to leverage the component nesting without having to use a nested URL.
    // 这允许你利用组件嵌套，而无需使用嵌套 URL。
    if (!(menu.path.startsWith('/') || isHttpUrl(menu.path))) {
      // path doesn't start with /, nor is it a url, join parent path
      // 路径不以 / 开头，也不是 url，加入父路径
      menu.path = `${parentPath}/${menu.path}`;
    }
    if (menu?.children?.length) {
      joinParentPath(menu.children, menu.meta?.hidePathForChildren ? parentPath : menu.path);
    }
  }
}
// 菜单路径处理
export function transformMenuModules(routeModList: Menu[]) {
  const cloneMenuModules = cloneDeep(routeModList);
  // 路径处理
  joinParentPath(cloneMenuModules);
  return cloneMenuModules;
}

// 将路由转换成菜单
export function transformRouteToMenu(routeModList: AppRouteModule[], routerMapping = false) {
  // 借助 lodash 深拷贝
  const cloneRouteModList = cloneDeep(routeModList);
  const routeList: AppRouteRecordRaw[] = [];

  // 对路由项进行修改
  cloneRouteModList.forEach((item) => {
    if (routerMapping && item.meta.hideChildrenInMenu && typeof item.redirect === 'string') {
      item.path = item.redirect;
    }

    if (item.meta?.single) {
      const realItem = item?.children?.[0];
      realItem && routeList.push(realItem);
    } else {
      routeList.push(item);
    }
  });
  // 提取树指定结构
  const list = treeHelper.treeMap(routeList, {
    conversion: (node: AppRouteRecordRaw) => {
      const { meta: { hideMenu = false } = {}, name } = node;

      return {
        ...(node.meta || {}),
        meta: node.meta,
        name,
        hideMenu,
        path: node.path,
        ...(node.redirect ? { redirect: node.redirect } : {}),
      };
    },
  });
  // 路径处理
  joinParentPath(list);
  return cloneDeep(list);
}

function configureDynamicParamsMenu(menu: Menu, params: RouteParams) {
  const path = menu.path;
  const paramPath = menu.paramPath;
  const realPath = paramPath || path;

  if (realPath.indexOf(':') === -1) {
    menu.children?.length && configureDynamicParamsMenuBatch(menu.children, params);
    return;
  }

  const paramMatches: Array<{ name: string; placeholder: string }> = [];
  for (const key in params) {
    if (Object.hasOwnProperty.call(params, key)) {
      const placeholder = `:${key}`;
      if (realPath.indexOf(placeholder) !== -1) {
        paramMatches.push({ name: key, placeholder });
      }
    }
  }
  if (paramMatches.length === 0) {
    menu.children?.length && configureDynamicParamsMenuBatch(menu.children, params);
    return;
  }

  let newRealPath = realPath;
  let hasValidParams = false;
  for (const { name, placeholder } of paramMatches) {
    const paramValue = params[name].toString();
    if (paramValue) {
      newRealPath = newRealPath.replaceAll
        ? newRealPath.replaceAll(placeholder, paramValue)
        : newRealPath.replace(new RegExp(placeholder, 'g'), paramValue);
      hasValidParams = true;
    }
  }
  if (!paramPath && paramMatches.length > 0) {
    menu.paramPath = path;
  }
  if (hasValidParams) {
    menu.path = newRealPath;
  }

  menu.children?.length && configureDynamicParamsMenuBatch(menu.children, params);
}

export function configureDynamicParamsMenuBatch(menus: Menu[], params: RouteParams) {
  if (!params || Object.keys(params).length === 0) return;

  for (let i = 0; i < menus.length; i++) {
    configureDynamicParamsMenu(menus[i], params);
  }
}
